/**
 * © [2022] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CommonModal from '../../../../SharedModules/Dialog/CommonModal';
import { NDC_LIMIT_TO_1000 } from '../../../../SharedModules/Errors/constantMessageData';
import SVGComponent from '../../../../SharedModules/SVGComponents/SVGComponents';
import Grid from '../../../../SharedModules/Table/tableGrid';
import TableFilterHeader from '../../../contactType/components/TableFilterHeader';
import NDCColumnCustomComponent from '../../NDC/component/NDCColumnCustomComponent';
import { NDC_DRUG_TABLEHAD } from '../../NDC/utility/tableHeads';
import {
  dispatchErrorMessages,
  getCalculatedData,
  setURADetailsTableRowSelected,
} from '../store/actions';

const URADetailsTable = ({
  showFullScreen = true,
  fullScreen,
  setFullScreen,
  setError,
  validateForm,
  setClickedViewButton,
}) => {
  const dispatch = useDispatch();
  const URADetailsTableData = useSelector(
    (state) => state.sharedState.URASearchReducer.URADetailsTableData
  );
  const URASearchFormValues = useSelector(
    (state) => state.sharedState.URASearchReducer.URASearchFormValues
  );
  const calculatedURAHeaderDataList = useSelector(
    (state) => state.sharedState.URASearchReducer.calculatedURAHeaderDataList
  );

  const childRef = useRef(null);
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [tableCount, setTableCount] = useState(0);
  const [applyFilter, setApplyFilter] = useState(false);
  const [confirmAction, setConfirmAction] = React.useState(false);

  const resetFilters = () => {
    gridApi && gridApi.setFilterModel(null);
  };

  const handleViewDetailsClick = async () => {
    if (validateForm()) {
      if (URADetailsTableData.length === 0) {
        dispatch(
          dispatchErrorMessages([
            'You have not selected any NDCs to view the calculated URA.',
          ])
        );
      } else if (URADetailsTableData.length > 1000) {
        setConfirmAction(true);
        return;
      } else {
        const requestData = {
          drugFamilyseqNum: ['-1'],
          ndcList: URADetailsTableData.map((item) => `${item.drugSeqNum}`),
          mfgIdList: ['-1'],
          rebateProgramCd: URASearchFormValues.rebateProgramCd,
          selectOneOption: 'ndc',
          planType:
            URASearchFormValues.planType === 'FD' ||
            URASearchFormValues.planType === 'ST'
              ? 'G'
              : 'C',
          qtrType:
            URASearchFormValues.quarters === 'all'
              ? 'ALL'
              : URASearchFormValues.quarters === 'target'
              ? 'TARGET'
              : 'CALC',
          ...(URASearchFormValues.quarters !== 'all' && {
            startQtr: URASearchFormValues.quarter,
          }),
        };
        dispatch(getCalculatedData(requestData));
        setClickedViewButton(true);
      }
    }
  };

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    setTableCount(params.api.getDisplayedRowCount());
  }, []);

  useEffect(() => {
    resetFilters();
    setTableCount(gridApi && gridApi.getDisplayedRowCount());
  }, [gridApi, URADetailsTableData]);

  const onSelectionChanged = useCallback(() => {
    dispatch(setURADetailsTableRowSelected(gridApi.getSelectedRows()));
  }, [gridApi]);

  return (
    <>
      <div className="d-flex align-items-center justify-content-end">
        <Button
          className="btn btn-success btn-view"
          onClick={() => handleViewDetailsClick()}
        >
          <SVGComponent iconName="Icon-view" />
          View Calculated URA
        </Button>
      </div>
      <TableFilterHeader
        gridApi={gridApi}
        columnApi={columnApi}
        childRef={childRef}
        showFullScreen={showFullScreen}
        applyFilter={applyFilter}
        fullScreen={fullScreen}
        setFullScreen={setFullScreen}
        showHistory={false}
        fileName="NDC Details"
        resetFilterFunc={resetFilters}
        className="mt-2 mb-1 border-bottom"
      />
      <CommonModal
        open={confirmAction}
        viewAction
        className="dialog-420"
        viewHeader
        onOk={() => {
          setConfirmAction(false);
          const requestData = {
            drugFamilyseqNum: ['-1'],
            ndcList: URADetailsTableData.map(
              (item) => `${item.drugSeqNum}`
            ).filter((obj, ind) => ind < 1000),
            mfgIdList: ['-1'],
            rebateProgramCd: URASearchFormValues.rebateProgramCd,
            selectOneOption: 'ndc',
            planType:
              URASearchFormValues.planType === 'FD' ||
              URASearchFormValues.planType === 'ST'
                ? 'G'
                : 'C',
            qtrType:
              URASearchFormValues.quarters === 'all'
                ? 'ALL'
                : URASearchFormValues.quarters === 'target'
                ? 'TARGET'
                : 'CALC',
            ...(URASearchFormValues.quarters !== 'all' && {
              startQtr: URASearchFormValues.quarter,
            }),
          };
          dispatch(getCalculatedData(requestData));
          setClickedViewButton(true);
        }}
        okTitle="Ok"
        closeTitle="Cancel"
        onClose={() => setConfirmAction(false)}
        modalTitle="Confirmation Dialog"
        childrenComponent={NDC_LIMIT_TO_1000}
      />
      <div className="table-container eightRecords">
        <Grid
          ref={childRef}
          gridHeight={fullScreen ? '100vh' : 350}
          rowData={URADetailsTableData ?? []}
          columnDefs={NDC_DRUG_TABLEHAD}
          defaultCol={{ editable: false }}
          onGridReady={onGridReady}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
          frameworkComponents={{ ndcComponent: NDCColumnCustomComponent }}
          onFilterChanged={(e) => {
            setTableCount(e.api.getDisplayedRowCount());
            if (
              (e.api.getFilterModel() && Object.keys(e.api.getFilterModel()))
                .length > 0
            ) {
              setApplyFilter(true);
            } else {
              setApplyFilter(false);
            }
          }}
        />
        <p className="total-results">
          Total Results <b>{tableCount?.toLocaleString('en-US')}</b>
        </p>
      </div>
    </>
  );
};

export default URADetailsTable;
